/**
 * Shared form primitives for the instance settings surface.
 *
 * Shared by the settings pages' sections (Perustiedot, Sisältö, Osallistujat,
 * Tietopohja) and field components (WorkshopGoalField, …).
 */
import styled from '@emotion/styled';

export const FormGroup = styled.div`
  margin-bottom: 24px;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  option {
    background: #1a1a1a;
    color: white;
  }
`;

export const FieldHelp = styled.p`
  margin: 8px 0 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.55);
`;

// Buttons and status lines for the settings pages (specs/admin-instance-lifecycle.md).
export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

// Transient $props stay off the DOM, also when rendered as={Link} or as="a".
export const Button = styled('button', { shouldForwardProp: (prop) => !prop.startsWith('$') })`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: ${p => (p.$size === 'small' ? '32px' : '40px')};
  padding: ${p => (p.$size === 'small' ? '5px 12px' : '9px 18px')};
  border-radius: 8px;
  font-size: ${p => (p.$size === 'small' ? '0.85rem' : '0.95rem')};
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => (p.$variant === 'danger' ? 'rgba(244, 67, 54, 0.4)' : p.$variant === 'primary' ? '#2563eb' : p.$variant === 'ghost' ? 'transparent' : 'rgba(255, 255, 255, 0.2)')};
  background: ${p => (p.$variant === 'danger' ? 'rgba(244, 67, 54, 0.15)' : p.$variant === 'primary' ? '#2563eb' : p.$variant === 'ghost' ? 'transparent' : 'rgba(255, 255, 255, 0.06)')};
  color: ${p => (p.$variant === 'danger' ? '#ff8a80' : p.$variant === 'ghost' ? 'rgba(255, 255, 255, 0.55)' : '#fff')};

  /* ghost: quiet until hovered; a destructive ghost turns red only then */
  &:hover:not(:disabled) { ${p => (p.$variant === 'ghost' ? `color: ${p.$tone === 'danger' ? '#ff8a80' : '#fff'}; background: ${p.$tone === 'danger' ? 'rgba(244, 67, 54, 0.12)' : 'rgba(255, 255, 255, 0.08)'};` : '')} }

  &:hover:not(:disabled) { filter: brightness(1.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  svg { width: ${p => (p.$size === 'small' ? '14px' : '16px')}; height: ${p => (p.$size === 'small' ? '14px' : '16px')}; }
`;

export const StatusLine = styled.span`
  font-size: 13px;
  color: ${p => (p.$error ? '#f87171' : '#4ade80')};
`;

export const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;

  > div { flex: 1; min-width: 0; }
  strong { display: block; font-size: 14px; font-weight: 600; }
  small { display: block; margin-top: 2px; font-size: 12px; color: rgba(255, 255, 255, 0.55); }
`;
